import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { SessionTimeoutDialogComponent } from './session-timeout-dialog.component';
import { IdleControlService } from '../idle-control.service';
import { messageService } from '../message.service';
import { AppService } from '../app.service';

enum SessionState { ACTIVE = 'ACTIVE', WARNING = 'WARNING', EXPIRED = 'EXPIRED' }

@Injectable({
  providedIn: 'root'
})

export class SessionTimeoutService implements OnDestroy {

  constructor(
    private readonly idle: Idle,
    private readonly dialog: MatDialog,
    private readonly keepalive: Keepalive,
    private readonly message: messageService,
    private readonly idleControl: IdleControlService,
    private readonly appService: AppService,
  ) { }

  private dialogRef: MatDialogRef<SessionTimeoutDialogComponent> | null = null;
  private readonly idleStateSubject = new BehaviorSubject<number>(0);
  private readonly destroy$ = new Subject<void>();
  private stop$ = new Subject<void>();
  private readonly oneQmsChannel = new BroadcastChannel('one-qms-channel');
  public idleState$ = this.idleStateSubject.asObservable();
  private logoutRef: (() => void) | null = null;
  private readonly warningSeconds = 60;
  private sessionState: SessionState = SessionState.ACTIVE;
  private idleExpiryAt: number | null = null;
  private countdownTimer: any = null;
  private idleSeconds!: number; // configured idle time
  private idleDetectedAt: number | null = null;
  private started = false;
  private lastRedisRefreshAt = 0;

  private clear_memory(): void {
    this.idleExpiryAt = null;
    this.idleDetectedAt = null;
    this.idleSeconds = 0;
  }

  /* ---------------- LOG ---------------- */
  /* This log is used to detect the session timeout activity of one-qms app
  This log is commented always since it will console every 5 second. Whenever necessary uncomment and comment it back before committing the code.
  WARNING :  ∟∟∟∟∟∟∟∟∟∟∟∟ ∟∟∟∟∟∟∟∟∟∟∟∟ Please do not remove this log ∟∟∟∟∟∟∟∟∟∟∟∟ ∟∟∟∟∟∟∟∟∟∟∟∟ */
  private sessionTimeoutLog(step: string, extra?: any): void {
    //console.log(`[SESSION]💢[${new Date().toISOString()}]💢[${step}]`, extra ?? '');
  }

  /* ---------------- TIMEOUT TRIGGER ---------------- */
  /* Initializing Session Timeout Function */
  public start(logout: () => void): void {
    this.sessionTimeoutLog('Initializing Session Timeout Program...');

    if (this.started) return;
    this.started = true;

    this.sessionState = SessionState.ACTIVE;
    this.clear_memory();
    this.idleStateSubject.next(0);

    /* The idle timeout is configured from the Organization Settings.
    If the value is not defined at the organization level, a default of 5 minutes is applied.
    The value is retrieved in minutes and converted to seconds before use */
    this.idleSeconds = this.message.logoutInMinutes ? Number.parseInt(this.message.logoutInMinutes, 10) * 60 : 60;
    this.sessionTimeoutLog('CONFIG', { organizationConfigured: this.idleSeconds, warningSeconds: this.warningSeconds });
    this.initializeBroadcast();
    this.logoutRef = logout;

    /* Setting Idle seconds using inbuilt function */
    this.idle.setIdle(this.idleSeconds);

    /* Setting Interrupts using inbuilt function which detects any hardware device movements */
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    /*@ng-idle/core exposes events as RxJS observables
    An observable stream that emits once every time ng-idle decides the user has become idle.
    Idle means: no mouse, no keyboard, no scroll, no configured, interrupt events → for idleSeconds
    It does not mean tab hidden. It does not mean API running. Only: no interaction events*/

    /* takeUntil(this.destroy$) Keep listening to this observable until destroy$ emits, then automatically unsubscribe.
    Prevents: memory leaks, duplicate idle triggers, callbacks firing after the component is destroyed, ghost popups on navigation.
    Without it: every time the component is created, you add another listener, after 3 navigation, onIdleDetected() fires 3 times
    timers start multiple times, chaos*/
    this.idle.onIdleStart
      .pipe(takeUntil(this.destroy$), takeUntil(this.stop$))
      .subscribe(() => this.onIdleDetected(logout));

    /*Taking Idle control to stop when API is running and start watching when API is completed*/
    this.idleControl.pause$
      .pipe(takeUntil(this.destroy$), takeUntil(this.stop$))
      .subscribe((paused: any) => {
        if (paused) {
          this.sessionTimeoutLog('API_PAUSE');
          this.idle.stop();
        } else {
          this.sessionTimeoutLog('API_RESUME');
          this.resetIdleAnchor();
          this.idle.watch();
        }
      });

    this.idleControl.apiActivity$
      .pipe(takeUntil(this.destroy$), takeUntil(this.stop$))
      .subscribe(() => {
        this.lastRedisRefreshAt = Date.now();
      });

    this.idle.onInterrupt
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.stop$),
        debounceTime(500) // prevent spam
      )
      .subscribe(source => {

        if (source instanceof StorageEvent) return;

        if (this.sessionState === SessionState.ACTIVE) {
          this.clearIdleCheck();
          this.resetIdleAnchor();
          if (this.logoutRef) { this.scheduleIdleCheck(this.logoutRef); }
          this.sessionTimeoutLog('OneQMS detected interaction 🚩', source);

          const now = Date.now();
          const logoutMinutes = Number(this.message.logoutInMinutes || 5);
          /* refresh at half timeout */
          const refreshThresholdMs = (logoutMinutes * 60 * 1000) / 2;
          /* enough time passed? */
          if (now - this.lastRedisRefreshAt >= refreshThresholdMs) {
            this.lastRedisRefreshAt = now;
            this.refreshRedis();
          }
        }
      });

    // Ping every 5 seconds (diagnostic only)
    this.keepalive.interval(5);

    // ✅ Also fix the ping: check based on sessionState, not idleExpiryAt
    this.keepalive.onPing
      .pipe(takeUntil(this.destroy$), takeUntil(this.stop$))
      .subscribe(() => {
        this.sessionTimeoutLog('OneQMS is watching your interactions 👀');
      });

    /* Initial reset and star watching*/
    this.resetIdleAnchor();
    this.idle.watch();
    if (this.logoutRef) { this.scheduleIdleCheck(this.logoutRef); }
  }

  /* ---------------- IDLE DETECTED ---------------- */
  /* This is a decision making function that triggers when the user should have become idle and what time it is now,
    do I still have time to warn them — and if so, how much?*/
  private onIdleDetected(logout: () => void): void {

    /* Guard clause. This prevents double execution. Idle detection is only meaningful when the session is ACTIVE.*/
    if (this.sessionState !== SessionState.ACTIVE) return;

    const now = Date.now();

    /* idleDetectedAt =“The exact time when idle should have started”
      now = “The moment the browser finally said about it”
      elapsedAfterIdle = how much idle time passed before user woke up*/
    const elapsedAfterIdle = Math.max(now - this.idleDetectedAt!, 0);

    /*remaining warning = total warning − time already lost*/
    const remainingWarningMs = this.warningSeconds * 1000 - elapsedAfterIdle;

    this.sessionTimeoutLog('IDLE_START', {
      now: new Date(now).toISOString(),
      elapsedAfterIdleMs: elapsedAfterIdle
    });

    /* The user was away so long that even the warning window is gone hence logging out user forcefully by program */
    if (remainingWarningMs <= 0) {
      this.sessionTimeoutLog('IDLE_EXPIRED_BEFORE_WARNING');
      this.forceExpire(logout);
      return;
    }

    /*Transition state: ACTIVE → WARNING */
    this.sessionState = SessionState.WARNING;

    /*The exact moment in real time when the session must die.*/
    this.idleExpiryAt = now + remainingWarningMs;

    /* This ceil function used to display maximum round off time to the users */
    this.idleStateSubject.next(Math.ceil(remainingWarningMs / 1000));

    this.sessionTimeoutLog('WARNING_START', {
      remainingSeconds: this.idleStateSubject.value,
      expiresAt: new Date(this.idleExpiryAt).toISOString()
    });

    this.openPopup();
    this.startCountdownTimer(logout);
  }

  /* ---------------- POPUP ---------------- */
  private openPopup(): void {
    if (this.dialogRef) return;

    this.dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      width: 'auto',
      maxHeight: '400px',
      disableClose: true,
      hasBackdrop: true,
      panelClass: 'my-dialog',
      position: { top: '10px' }
    });

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }


  private closePopup(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  /* ---------------- COUNTDOWN ---------------- */
  private startCountdownTimer(logout: () => void): void {
    if (this.countdownTimer || !this.idleExpiryAt) return;

    this.sessionTimeoutLog('COUNTDOWN_START');

    this.countdownTimer = setInterval(() => {
      if (!this.idleExpiryAt) return;

      const remainingMs = this.idleExpiryAt - Date.now();
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      if (remainingSeconds <= 0) {
        this.idleStateSubject.next(0);
        this.sessionTimeoutLog('COUNTDOWN_END');
        this.forceExpire(logout);
        return;
      }

      this.idleStateSubject.next(remainingSeconds);
      this.sessionTimeoutLog('COUNTDOWN_TICK ⏱️ ', remainingSeconds);
    }, 1000);
  }

  private stopCountdownTimer(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /* ---------------- OK CLICK ---------------- */
  stay(): void {
    if (this.sessionState !== SessionState.WARNING) return;

    this.sessionTimeoutLog('USER_OK', { remainingSeconds: this.idleStateSubject.value });
    this.stopCountdownTimer();
    this.refreshRedis();

    this.sessionState = SessionState.ACTIVE;
    this.idleExpiryAt = null;
    this.idleStateSubject.next(0);

    this.clearIdleCheck();
    this.resetIdleAnchor();
    this.idle.watch();
    if (this.logoutRef) { this.scheduleIdleCheck(this.logoutRef); }
    this.closePopup();
  }

  /* ---------------- EXPIRE ---------------- */
  private forceExpire(logout: () => void): void {
    if (this.sessionState === SessionState.EXPIRED) return;

    this.sessionTimeoutLog('EXPIRE');

    this.stopCountdownTimer();
    this.sessionState = SessionState.EXPIRED;

    this.clearIdleCheck();
    this.closePopup();
    this.dialog.closeAll();
    logout();
  }

  private resetIdleAnchor(): void {
    this.idleDetectedAt = Date.now() + this.idleSeconds * 1000;
    this.sessionTimeoutLog('Resetting OneQMS clock... [Logging out by]', new Date(this.idleDetectedAt).toISOString());
  }

  ngOnDestroy(): void {
    this.sessionTimeoutLog('DESTROY');
    this.oneQmsChannel.close();
    this.destroy$.next();
    this.destroy$.complete();
    this.clearIdleCheck();
    this.stopCountdownTimer();
  }

  public stop(): void {
    if (!this.started) return;

    this.sessionTimeoutLog('STOP');

    this.stop$.next();
    this.stop$.complete();

    // recreate for next login/session
    this.stop$ = new Subject<void>();
    this.started = false;

    this.sessionState = SessionState.ACTIVE;
    this.idleExpiryAt = null;
    this.idleStateSubject.next(0);
    this.logoutRef = null;

    this.clearIdleCheck();
    this.idle.stop();
    this.stopCountdownTimer();
    this.closePopup();
  }

  private lastActivityAt = 0;

  public notifyActivity(): void {
    const now = Date.now();
    if (now - this.lastActivityAt < 1000) return;
    this.lastActivityAt = now;

    if (this.sessionState !== SessionState.ACTIVE) return;

    this.clearIdleCheck();
    this.resetIdleAnchor();
    this.idle.watch();
    if (this.logoutRef) {
      this.scheduleIdleCheck(this.logoutRef);
    }
  }

  private refreshRedis(): void {
    this.appService.refreshSession().subscribe({
      next: () => {
        this.oneQmsChannel.postMessage({ type: 'SESSION_REFRESH' });
        this.sessionTimeoutLog('SESSION REFRESHED');
      },
      error: () => {
        this.sessionTimeoutLog('SESSION REFRESH FAILED');
      }
    });
  }

  private initializeBroadcast(): void {

    this.oneQmsChannel.onmessage = (event) => {

      if (event.data?.type !== 'SESSION_REFRESH') {
        return;
      }

      if (this.sessionState === SessionState.EXPIRED) {
        return;
      }

      this.sessionTimeoutLog('SESSION_SYNC');

      if (this.sessionState === SessionState.WARNING) {
        this.stopCountdownTimer();
        this.closePopup();
        this.idleExpiryAt = null;
        this.idleStateSubject.next(0);
      }

      this.sessionState = SessionState.ACTIVE;

      this.resetIdleAnchor();
      this.idle.watch();
      this.scheduleIdleCheck(this.logoutRef!);
    };
  }

  private syncTimeoutRef: any = null;

  private scheduleIdleCheck(logout: () => void): void {

    clearTimeout(this.syncTimeoutRef);
    this.syncTimeoutRef =
      setTimeout(() => {
        if (this.sessionState === SessionState.ACTIVE) {
          this.onIdleDetected(logout);
        }

      }, this.idleSeconds * 1000);
  }

  private clearIdleCheck(): void {
    if (this.syncTimeoutRef) {
      clearTimeout(this.syncTimeoutRef);
      this.syncTimeoutRef = null;
    }
  }

}
